import { AccountInfo } from "@azure/msal-browser";
import { loginRequest } from "../msal/msalConfig";
import { msalInstance } from "../msal/msalInstance";
import {
  SAFETY_BUFFER_SECONDS,
  SUPERSET_STORAGE_KEYS,
} from "@/shared/constants";
import {
  beginSignOut,
  isSignedOutInAnotherTab,
  requestLoginRedirect,
} from "./authSync";

let isSessionRecoveryInProgress = false;

export const getToken = (): Promise<string> => {
  const getAccessToken = async (): Promise<string> => {
    if (isSignedOutInAnotherTab()) {
      throw new Error("Signed out in another tab.");
    }

    const account = msalInstance.getActiveAccount();
    if (!account) {
      if (!isSignedOutInAnotherTab()) {
        await requestLoginRedirect(loginRequest);
      }
      throw new Error("No account found. Redirecting to login.");
    }
    const exp = account.idTokenClaims?.exp;

    // If no expiry claim → force refresh
    if (!exp) {
      return refreshToken(account);
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);

    // Refresh if expired OR within safety buffer
    const isExpiringSoon = exp - nowInSeconds <= SAFETY_BUFFER_SECONDS;

    if (isExpiringSoon) {
      return refreshToken(account);
    }

    return account?.idToken || ""; // Return the token or an empty string if not available
  };

  return getAccessToken();
};

export const getSupersetAuthToken = async (
  useRefreshToken: boolean,
): Promise<string | null> => {
  const token = useRefreshToken
    ? localStorage.getItem(SUPERSET_STORAGE_KEYS.refreshToken)
    : localStorage.getItem(SUPERSET_STORAGE_KEYS.authToken);
  return token;
};

export const refreshToken = async (
  refAccount: AccountInfo,
): Promise<string> => {
  if (isSignedOutInAnotherTab()) {
    throw new Error("Signed out in another tab.");
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      account: refAccount,
      scopes: loginRequest.scopes,
      forceRefresh: true,
    });

    if (isSignedOutInAnotherTab()) {
      throw new Error("Signed out in another tab.");
    }

    return response.idToken;
  } catch (error) {
    if (isSignedOutInAnotherTab()) {
      throw error;
    }
    return forceLogoutAndRedirectToLogin();
  }
};

export const forceLogoutAndRedirectToLogin = async (): Promise<never> => {
  if (isSessionRecoveryInProgress) {
    throw new Error("Session recovery is already in progress.");
  }
  // Signed out in another tab: stay on this page until the user acts here.
  if (isSignedOutInAnotherTab()) {
    throw new Error("Signed out in another tab.");
  }
  isSessionRecoveryInProgress = true;

  const activeAccount = msalInstance.getActiveAccount();

  try {
    if (activeAccount) {
      beginSignOut();
      await msalInstance.logoutRedirect({
        account: activeAccount,
        postLogoutRedirectUri: window.location.origin,
      });
    } else {
      await requestLoginRedirect(loginRequest);
    }
  } catch {
    try {
      await requestLoginRedirect(loginRequest);
    } catch {
      isSessionRecoveryInProgress = false;
      throw new Error("Session recovery failed. Unable to redirect to login.");
    }
  }

  throw new Error("Session expired. Redirecting to login.");
};
